import type { ResultMatchFromBackend } from 'app/api/services/matches/match-result/types';

import { NOTIFY_ABOUT_CHANGED_LANG, NotifyAboutChangedLang } from 'app/lang';
import type {
  BackendEntitiesDiff,
  BackendMatch,
  BackendObjects,
  RecordOfBettingEntitiesState,
  SSRBettingEntitiesState,
} from 'app/lib/betting/betting-entities';
import { TEAMS } from 'app/lib/betting/constants';
import { INIT_ACTION } from 'app/redux/actions/init';
import { TRANSFORM_SSR_STATE, TransformSSRState } from 'app/redux/ssrState';

import {
  getMatchResultDetails,
  MatchResultDetailsFailure,
  MatchResultDetailsPending,
  MatchResultDetailsSuccess,
} from '../service';
import { reduceGrid } from './entities';

// ---

type MatchResultReducer = {
  entities?: RecordOfBettingEntitiesState;
};

const initialState: MatchResultReducer = {
  entities: reduceGrid(undefined, INIT_ACTION),
};

type MatchResultActions =
  | TransformSSRState<SSRBettingEntitiesState>
  | MatchResultDetailsPending
  | MatchResultDetailsSuccess
  | MatchResultDetailsFailure
  | NotifyAboutChangedLang;

function createBettingEntitiesStructure(action: MatchResultDetailsSuccess) {
  type SuccessPayload = MatchResultDetailsSuccess['payload']['data'];

  const objects: BackendObjects = {};

  const keys = Object.keys(action.payload.data);

  const diff = keys.reduce((acc: BackendEntitiesDiff, key) => {
    const value = action.payload.data[key as keyof SuccessPayload];

    acc[key as keyof SuccessPayload] = {};

    const ids = Object.keys(value);
    ids.forEach((entityId) => {
      const entity = value[entityId];
      const matchObjects: BackendMatch['objects'] = {};

      if (key === 'matches') {
        Object
          .entries<string>((entity as ResultMatchFromBackend).objects)
          .forEach(([objectId, name], index) => {
            const id = Number(objectId);
            const teamId = TEAMS[index];

            matchObjects[teamId] = id;
            objects[objectId] = { id, name };
          });
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      acc[key][entityId] = { ...entity, objects: matchObjects, id: Number(entityId) };
    });

    return acc;
  }, {});

  diff.objects = objects;

  return {
    type: action.type,
    payload: { diff },
  };
}

function matchResultReducer(state = initialState, action: MatchResultActions): MatchResultReducer {
  switch (action.type) {
    case TRANSFORM_SSR_STATE:
    case NOTIFY_ABOUT_CHANGED_LANG:
    case getMatchResultDetails.pending.toString():
    case getMatchResultDetails.rejected.toString():
      return {
        ...state,
        entities: reduceGrid(state.entities, action),
      };

    case getMatchResultDetails.fulfilled.toString():
      return {
        ...state,
        entities: reduceGrid(state.entities, createBettingEntitiesStructure(
          action as MatchResultDetailsSuccess,
        )),
      };

    default:
      return state;
  }
}


export default matchResultReducer;
